import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../common/Header';
import ShipmentsTable from '../common/ShipmentsTable';
import EmailListInput, {
  pickEmailToFetchPdf,
  extractApiErrorMessage,
  isEmailToFetchPdfError,
} from '../common/EmailListInput';
import SeaExportTableReadOnly from './SeaExportTableReadOnly';
import PendingApprovalQueue from './PendingApprovalQueue';
import InvoiceGenerationPage from './InvoiceGenerationPage';
import HsnCodeManager from '../common/HsnCodeManager';
import ClientMaster from '../common/ClientMaster';
import AdminShell from '../Layout/AdminShell';
import SuperAdminUsers from './SuperAdminUsers';
import AddUserPage from './AddUserPage';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { PageHead, Panel, Pill, btn, field, MultiSelect, StatCard, StatRow, Table, Row, Cell, Avatar, Select, Modal, FormField, ConfirmModal, FormSection } from '../common/AdminUI';
import { CustomerInvoicesModal } from '../common/CustomerInvoices';
import ShipmentFilters from '../common/ShipmentFilters';
import { matchesShipmentSearch } from '../../utils/shipmentSearch';
import { makePaymentMatcher } from '../common/InvoiceFilters';
import { UserPlus, Users, Package, Edit, Eye, EyeOff, RefreshCw, Download, Wallet, PlusCircle, Lock, Unlock, History, ChevronDown, ChevronRight, X, Inbox, Hash, FileText, Plane, Ship, RotateCcw, Search, Calendar, RotateCw, CheckCircle2, AlertTriangle, ArrowRight, UserCog, KeyRound, Mail, Save, User as UserIcon, Receipt } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, apiCall } from '../../config/api';
import { hashPassword } from '../../utils/crypto';
import { useAuth } from '../../context/AuthContext';
import { sendWalletUpdateEmail } from '../../utils/emailService';
import { escapeCsv } from '../../utils/exportCsv';

const SuperAdminDashboard = ({ allUsers, setAllUsers }) => {
  const navigate = useNavigate();
  const { getToken, fetchUsers, allUsers: globalUsers, user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [consoleSearch, setConsoleSearch] = useState('');   // the users tab
  const [hsnSearch, setHsnSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  // All Shipments filter bar
  const [shipSearch, setShipSearch] = useState('');
  const [shipDests, setShipDests] = useState([]);
  const [shipFrom, setShipFrom] = useState('');
  const [shipTo, setShipTo] = useState('');
  const [showShipDates, setShowShipDates] = useState(false);
  // The billing filters the admin and accountant consoles already had.
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [unpaidSince, setUnpaidSince] = useState(null);
  const [showUnpaidSincePicker, setShowUnpaidSincePicker] = useState(false);
  const [overdueDays, setOverdueDays] = useState('');
  const [kpiFilter, setKpiFilter] = useState('all');   // 'all' | 'billed' | 'unbilled'
  const [shipBook, setShipBook] = useState(null);   // every consignment, for the figures, the filters and filtered views
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0);

  // Lightweight, independent of PendingApprovalQueue mounting — so the tab badge
  // shows a count immediately on load, not only after the user opens that tab.
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall(`${API_ENDPOINTS.GET_ALL_SHIPMENTS}?approvalStatus=PENDING_APPROVAL&limit=1&offset=0`, { method: 'GET' });
        const result = await res.json();
        if (res.ok && result.success) setPendingApprovalCount(result.data?.totalRecords || 0);
      } catch {
        // badge count is a convenience — fail silently
      }
    })();
  }, []);
  const [shipmentType, setShipmentType] = useState('Air-Export');
  const [seaExportShipments, setSeaExportShipments] = useState([]);
  const [loadingSeaExport, setLoadingSeaExport] = useState(false);

  // ── Wallet Management State ──────────────────────────────────────────────
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [expandedWallet, setExpandedWallet] = useState(null);
  const [walletTxns, setWalletTxns] = useState({});
  const [walletTxnsLoading, setWalletTxnsLoading] = useState({});
  const [creatingWalletFor, setCreatingWalletFor] = useState(new Set());
  const [creditModal, setCreditModal] = useState(null); // { walletId, userName }
  const [creditAmount, setCreditAmount] = useState('');
  const [creditSource, setCreditSource] = useState('cash');
  const [creditRemarks, setCreditRemarks] = useState('');
  const [creditLoading, setCreditLoading] = useState(false);
  const [walletSearch, setWalletSearch] = useState('');
  const [walletStatusFilter, setWalletStatusFilter] = useState('all');
  const [walletSort, setWalletSort] = useState('recent');

  const fmt = (rupees) => `₹${Number(rupees || 0).toLocaleString('en-IN')}`;
  const paiseToRupees = (p) => (Number(p || 0) / 100);

  const fetchWallets = async () => {
    setWalletsLoading(true);
    try {
      const res = await apiCall(API_ENDPOINTS.WALLET_ALL);
      const data = await res.json();
      if (res.ok && data.success) setWallets(data.data || []);
      else toast.error(data.message || 'Failed to load wallets');
    } catch { toast.error('Error fetching wallets'); }
    finally { setWalletsLoading(false); }
  };

  const fetchWalletTxns = async (walletId) => {
    if (walletTxns[walletId]) return;
    setWalletTxnsLoading(prev => ({ ...prev, [walletId]: true }));
    try {
      const res = await apiCall(`${API_ENDPOINTS.WALLET_ALL}/${walletId}`);
      const data = await res.json();
      if (res.ok && data.success) {
        setWalletTxns(prev => ({ ...prev, [walletId]: data.data?.transactions || [] }));
      }
    } catch { /* ignore */ }
    finally { setWalletTxnsLoading(prev => ({ ...prev, [walletId]: false })); }
  };

  const handleCreateWalletForUser = async (userId, userName) => {
    setCreatingWalletFor(prev => new Set([...prev, userId]));
    try {
      const res = await apiCall(API_ENDPOINTS.WALLET_CREATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: userId }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Wallet created for ${userName}`);
        fetchWallets();
      } else {
        toast.error(data.message || 'Failed to create wallet');
      }
    } catch { toast.error('Error creating wallet'); }
    finally { setCreatingWalletFor(prev => { const n = new Set(prev); n.delete(userId); return n; }); }
  };

  const handleCredit = async (e) => {
    e.preventDefault();
    const amtRupees = parseFloat(creditAmount);
    if (!amtRupees || amtRupees <= 0) { toast.error('Enter a valid amount'); return; }
    setCreditLoading(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.WALLET_ALL}/${creditModal.walletId}/credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_paise: Math.round(amtRupees * 100),
          source: creditSource,
          remarks: creditRemarks || undefined,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`₹${amtRupees} credited to ${creditModal.userName}`);
        sendWalletUpdateEmail({
          to_email: creditModal.userEmail || '',
          name: creditModal.userName || 'User',
          transaction_type: 'Credit',
          amount: `₹${amtRupees}`,
          transaction_id: data.data?.id || '',
          wallet_balance: data.data?.balancePaise != null ? `₹${(data.data.balancePaise / 100).toFixed(2)}` : '',
          description: creditRemarks || `Wallet credited by admin`,
        }).catch(console.error);
        setCreditModal(null);
        setCreditAmount('');
        setCreditRemarks('');
        setCreditSource('cash');
        setWalletTxns(prev => { const n = { ...prev }; delete n[creditModal.walletId]; return n; });
        fetchWallets();
      } else {
        toast.error(data.message || 'Credit failed');
      }
    } catch { toast.error('Error crediting wallet'); }
    finally { setCreditLoading(false); }
  };

  const handleToggleStatus = async (walletId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'blocked' : 'active';
    try {
      const res = await apiCall(`${API_ENDPOINTS.WALLET_ALL}/${walletId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Wallet ${newStatus === 'active' ? 'unblocked' : 'blocked'}`);
        fetchWallets();
      } else {
        toast.error(data.message || 'Update failed');
      }
    } catch { toast.error('Error updating wallet status'); }
  };

  // CSV Export function
  const exportToCSV = (data, filename = 'shipments.csv') => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    const formatAwbForCsv = (awb) => {
      if (awb === null || awb === undefined || awb === '') return '';
      const str = String(awb);
      return str.startsWith('0') ? `="${str}"` : str;
    };


    // Define CSV headers - matching all table columns
    const headers = [
      'AWB Number', 'Username', 'Customer Name', 'SB Number', 'SB Date',
      'City Code', 'Dest City', 'Dest Country', 'Flight No', 'Flight Date',
      'Total Pkt', 'Weight', 'CH Weight', 'Amount', 'Activity', 'FOB',
      'DBK', 'Rodtep', 'Rosctl', 'IGST', 'FC Value', 'FC Type', 'FC Rate', 'Buyer Name',
      'AWB', 'LEO', 'INV/PL', 'INVOICE', 'PUR',
      'Admin Name', 'Emp Name'
    ];

    // Convert data to CSV rows - matching all table data
    const csvRows = data.map(shipment => [
      formatAwbForCsv(shipment.awbNumber || ''),
      shipment.username || '',
      shipment.customerName || '',
      shipment.sbNumber || '',
      shipment.sbDate || '',
      shipment.cityCode || '',
      shipment.destCity || '',
      shipment.destCountry || '',
      shipment.flightNo || '',
      shipment.flightDate || '',
      shipment.totalPkt || '',
      shipment.weight || '',
      shipment.chWeight || '',
      shipment.amount || '',
      shipment.activity || '',
      shipment.fob || '',
      shipment.dbk || '',
      shipment.rodtep || '',
      shipment.rosctl || '',
      shipment.igst || '',
      shipment.fcValue || '',
      shipment.fcType || '',
      shipment.fcRate || '',
      shipment.buyerName || '',
      shipment.awb ? 'Yes' : 'No',
      shipment.leo ? 'Yes' : 'No',
      shipment.invpl ? 'Yes' : 'No',
      shipment.invoice ? 'Yes' : 'No',
      shipment.pur ? 'Yes' : 'No',
      shipment.adminName || '',
      shipment.editedBy || ''
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(escapeCsv).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showInvoiceGeneration, setShowInvoiceGeneration] = useState(false);
  const [invoiceShipment, setInvoiceShipment] = useState(null);
  const [customerInvoiceOpen, setCustomerInvoiceOpen] = useState(false);
  const [customerInvoiceEdit, setCustomerInvoiceEdit] = useState(null);
  const [customerInvoiceListOpen, setCustomerInvoiceListOpen] = useState(false);
  const [invoiceMap, setInvoiceMap] = useState({});        // awb/bl -> invoice records
  const [allInvoicesList, setAllInvoicesList] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [shipmentsLoading, setShipmentsLoading] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [editApiError, setEditApiError] = useState('');
  const [editUser, setEditUser] = useState({
    customerName: '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
    role: 'employee',
    status: 'active',
    website: '',
    emailToFetchPdf: []
  });
  const [expandedAdmins, setExpandedAdmins] = useState({});

  // Pagination state
  const PAGE_SIZE = 20;
  const [airHasMore, setAirHasMore] = useState(true);
  // The server counts rows as it stores them; the table shows fewer, because
  // the ones awaiting approval are dropped on arrival. Paging off the rendered
  // count therefore asks from the wrong place and the pages drift.
  const airRawCount = useRef(0);
  const [airLoadingMore, setAirLoadingMore] = useState(false);
  const [seaHasMore, setSeaHasMore] = useState(true);
  const [seaLoadingMore, setSeaLoadingMore] = useState(false);
  const fetchShipmentsRef = useRef(null);
  const airSentinelRef = useRef(null);
  const seaSentinelRef = useRef(null);

  const getDisplayRole = (role) => {
    return role;
  };

  const normalizeValue = (value) => String(value || '').trim().toLowerCase();

  const normalizePhoneNumber = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    if (digits.length > 10) {
      return digits.startsWith('91') ? digits.slice(-10) : digits.slice(-10);
    }
    return digits;
  };

  const getUniqueList = (items) => {
    const seen = new Set();
    return items.filter(item => {
      const key = normalizeValue(item);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Options list every value in the book, not only the page that has loaded.
  const getCustomerOptions = () => {
    if (shipmentType === 'Sea-Export') {
      return getUniqueList((shipBook?.sea || seaExportShipments).map(s => s.exporterName).filter(Boolean));
    }
    return getUniqueList((shipBook?.air || shipments).map(s => s.customerName).filter(Boolean));
  };

  const getAdminOptions = () => (shipmentType === 'Sea-Export'
    ? getUniqueList((shipBook?.sea || seaExportShipments).map(s => s.admin_name).filter((v) => v && v !== '-'))
    : getUniqueList((shipBook?.air || shipments).map(s => s.adminName).filter(Boolean)));

  const toggleCustomerSelection = (name) => {
    setSelectedCustomer(prev => {
      const exists = prev.some(c => normalizeValue(c) === normalizeValue(name));
      return exists ? prev.filter(c => normalizeValue(c) !== normalizeValue(name)) : [...prev, name];
    });
    setSelectedAdmin([]);
  };

  const toggleAdminSelection = (name) => {
    setSelectedAdmin(prev => {
      const exists = prev.some(a => normalizeValue(a) === normalizeValue(name));
      return exists ? prev.filter(a => normalizeValue(a) !== normalizeValue(name)) : [...prev, name];
    });
    setSelectedCustomer([]);
  };

  useEffect(() => {
    const handleClickOutside = () => {};

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleAdminExpand = (adminId) => {
    setExpandedAdmins(prev => ({
      ...prev,
      [adminId]: !prev[adminId]
    }));
  };

  // Get admins with their child users
  const getAdminsWithChildren = () => {
    // Use allUsers prop if available, otherwise use globalUsers from context
    const usersArray = allUsers.length > 0 ? allUsers : globalUsers;
    console.log('Total users for hierarchy:', usersArray.length);
    
    const admins = usersArray.filter(u => u.role === 'admin').sort((a, b) => b.id - a.id);
    console.log('Total admins found:', admins.length);
    
    return admins.map(admin => {
      const roleOrder = { employee: 0, accountant: 1, user: 2 };
      const children = usersArray
        .filter(u => u.created_by === admin.id && u.role !== 'admin')
        .sort((a, b) => {
          const ra = roleOrder[getDisplayRole(a.role)] ?? 3;
          const rb = roleOrder[getDisplayRole(b.role)] ?? 3;
          if (ra !== rb) return ra - rb;
          return b.id - a.id; // same role → newest first
        });
      console.log(`Admin ${admin.id} (${admin.username}) has ${children.length} children`);
      return {
        admin,
        children
      };
    });
  };

  // Fetch customers for shipments filter
  useEffect(() => {
    const fetchCustomersList = async () => {
      try {
        const response = await apiCall(API_ENDPOINTS.GET_CUSTOMERS, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && response.status === 200) {
          console.log('SuperAdmin Customers API Response:', data);
          console.log('Raw data array:', data.data);
          
          // data is directly an array
          let rawCustomers = data.data || [];
          
          // If rawCustomers is not an array, make it empty
          if (!Array.isArray(rawCustomers)) {
            console.log('rawCustomers is not array:', rawCustomers);
            rawCustomers = [];
          }
          
          const customersList = rawCustomers.map(c => ({
            id: c.id,
            name: c.our_users || c.custName || c.name || c.username || 'Unknown'
          }));
          console.log('SuperAdmin Mapped customers:', customersList);
          setCustomers(customersList);
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    };

    fetchCustomersList();
  }, []);

  // Helper functions for data formatting
  const parseField = (field) => {
    if (field === null || field === undefined || field === '') return '-';
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? parsed.join(', ') : parsed;
      } catch {
        return field;
      }
    }
    return field;
  };

  const joinArray = (arr) => {
    if (!arr || !Array.isArray(arr)) return '-';
    return arr.filter(item => item !== null && item !== undefined && item !== '').join(' & ') || '-';
  };

  const joinUniqueArray = (arr) => {
    if (!arr || !Array.isArray(arr)) return '-';
    const uniqueValues = [...new Set(arr.filter(item => item !== null && item !== undefined && item !== ''))];
    return uniqueValues.join(' & ') || '-';
  };

  // Helper function to format dates - convert to DD/MM/YYYY
  const formatDates = (arr) => {
    if (!arr || !Array.isArray(arr)) return '-';
    const filtered = arr.filter(item => item !== null && item !== undefined && item !== '');
    
    if (filtered.length === 0) return '-';
    
    // Month mapping
    const monthMap = {
      'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
      'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
    };
    
    // Convert dates to DD/MM/YYYY format first
    const formattedDates = filtered.map(date => {
      if (!date) return null;
      let dateStr = date;
      // Remove time part if present
      if (typeof date === 'string' && date.includes(' ')) {
        dateStr = date.split(' ')[0];
      }
      
      // Check if already in DD/MM/YYYY format
      const ddmmyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      const match = dateStr.match(ddmmyyyyPattern);
      if (match) {
        return dateStr; // Already in correct format
      }
      
      // Check for DD-MMM-YYY or DD-MMM-YYYY format (e.g., "15-NOV-025")
      const ddmmmyyyPattern = /^(\d{2})-(\w{3})-(\d{2,4})$/;
      const mmmMatch = dateStr.match(ddmmmyyyPattern);
      if (mmmMatch) {
        const day = parseInt(mmmMatch[1]);
        const monthStr = mmmMatch[2].toUpperCase();
        let year = parseInt(mmmMatch[3]);
        
        // Fix year: 025 -> 2025, 25 -> 2025
        if (year < 100) {
          year += 2000;
        }
        
        const month = monthMap[monthStr];
        if (month !== undefined) {
          return `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}/${year}`;
        }
      }
      
      // Parse and format to DD/MM/YYYY
      try {
        const dateObj = new Date(dateStr);
        if (!isNaN(dateObj.getTime())) {
          let day = dateObj.getDate();
          let month = dateObj.getMonth() + 1;
          let year = dateObj.getFullYear();
          
          // Fix for years like 025 (should be 2025)
          if (year < 100) {
            year += 2000;
          }
          
          return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
        }
      } catch (e) {
        console.error('Date parsing error:', e);
      }
      return null;
    }).filter(d => d !== null);
    
    // Remove duplicate formatted dates only
    const unique = [...new Set(formattedDates)];
    return unique.join(' & ') || '-';
  };

  // One row as the table reads it. Shared by the paged fetch and the
  // unpaginated read behind the figures and the filter lists.
  const formatAirRow = (shipment) => ({
      id: shipment.id,
      userId: shipment.userId || shipment.userID || shipment.user_id || null,
      awbNumber: shipment.awbNumber || shipment.awb || '',
      username: shipment.username || '',
      customerName: shipment.customerName || '',
      sbNumber: joinArray(shipment.sbNumbers),
      sbDate: formatDates(shipment.sbDates),
      portCode: shipment.portCode || '',
      cityCode: shipment.destCode || '',
      destCity: shipment.destCity || '',
      destCountry: shipment.destCountry || '',
      flightNo: parseAndJoinUniqueArray(shipment.flightNumbers),
      flightDate: parseAndJoinUniqueArray(shipment.flightDates),
      totalPkt: shipment.numberOfPackages || '-',
      weight: shipment.grossWeight || '-',
      chWeight: shipment.chargeableWeight || '-',
      amount: parseAndJoinArray(shipment.paymentsAmount),
      activity: parseAndJoinArray(shipment.paymentActivity),
      fob: parseAndJoinArray(shipment.sbFobValues),
      dbk: parseAndJoinArray(shipment.DBK ?? shipment.dbk ?? shipment.dbkClaim) || '-',
      rodtep: parseAndJoinArray(shipment.Rodtep ?? shipment.rodtep ?? shipment.rodtepAmt) || '-',
      rosctl: parseAndJoinArray(shipment.Rosctl ?? shipment.rosctl ?? shipment.rosctlAmt) || '-',
      igst: parseAndJoinArray(shipment.IGST ?? shipment.igst ?? shipment.igstAmt) || '-',
      fcValue: shipment.fcValue || '-',
      fcType: shipment.fcType || '-',
      fcRate: shipment.fcRate || '-',
      buyerName: shipment.buyerName || '-',
      awb: shipment.awbPdf || shipment.awb || '',
      leo: shipment.leoPdf || shipment.leo || '',
      inv: shipment.invPdf || shipment.inv || '',
      invoice: shipment.invoicePdf || shipment.invoice || '',
      pur: shipment.purPdf || shipment.pur || '',
      service: shipment.shipmentType || '',
      status: shipment.leoStatus || shipment.status || 'Pending',
      editedBy: shipment.editedBy || shipment.edited_by || '-',
      adminName: shipment.admin_name || '-'
  });

  const formatSeaRow = (s) => ({
    id: s.id,
    userId: s.user_id || s.userId || s.userID || null,
    portCode: s.port_code || s.portCode || '-',
    sbNo: s.sb_no || s.sbNo || '-',
    sbDate: s.sb_date || s.sbDate || '-',
    pkg: s.pkg || '-',
    gwt: s.gwt || '-',
    portOfLoading: s.port_of_loading || s.portOfLoading || '-',
    stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
    portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
    portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
    portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
    portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
    countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
    exporterName: s.exporter_name || s.exporterName || '-',
    buyerName: s.buyer_name || s.buyerName || '-',
    consigneeName: s.consignee_name || s.consigneeName || '-',
    mode: s.mode || '-',
    lut: s.lut || '-',
    fobValue: s.fob_value || s.fobValue || '-',
    dbkClaim: s.dbk_claim || s.dbkClaim || '-',
    igstAmt: s.igst_amt || s.igstAmt || '-',
    cessAmt: s.cess_amt || s.cessAmt || '-',
    igstValue: s.igst_value || s.igstValue || '-',
    rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
    rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
    invNo: s.inv_no || s.invNo || '-',
    invAmt: s.inv_amt || s.invAmt || '-',
    currency: s.currency || '-',
    leoDate: s.leo_date || s.leoDate || '-',
    invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
    blNumber: s.bl_number || s.blNumber || '-',
    containerNo: s.container_no || s.containerNo || '-',
    containerQty: s.container_qty || s.containerQty || '-',
    leoPdf: s.leo_pdf || s.leoPdf || '',
    blPdf: s.bl_pdf || s.blPdf || '',
    invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
    invoicePdf: s.invoice_pdf || s.invoicePdf || '',
    purPdf: s.pur_pdf || s.purPdf || '',
    admin_name: s.admin_name || s.adminName || '-',
    edited_by: s.edited_by || s.editedBy || '-',
    editedBy: s.edited_by || s.editedBy || '-',
    createdAt: s.created_at || s.createdAt || new Date().toISOString(),
    type: 'Sea-Export'
  });
  const parseAndJoinArray = (field) => {
    if (!field) return '-';
    if (Array.isArray(field)) return joinArray(field);
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? joinArray(parsed) : (parsed || '-');
      } catch {
        return field;
      }
    }
    return field;
  };

  const parseAndJoinUniqueArray = (field) => {
    if (!field) return '-';
    if (Array.isArray(field)) return joinUniqueArray(field);
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        return Array.isArray(parsed) ? joinUniqueArray(parsed) : (parsed || '-');
      } catch {
        return field;
      }
    }
    return field;
  };

  const getFirstElement = (arr) => {
    if (!arr || !Array.isArray(arr)) return '-';
    return arr[0] || '-';
  };

  // Fetch all shipments
  useEffect(() => {
    const fetchAllShipments = async (airOffset = 0, seaOffset = 0, append = false) => {
      // Fetch Air Export shipments
      if (!append) setShipmentsLoading(true);
      else setAirLoadingMore(true);
      try {
        const response = await apiCall(`${API_ENDPOINTS.GET_ALL_SHIPMENTS}?limit=${PAGE_SIZE}&offset=${airOffset}`, {
          method: 'GET'
        });

        const data = await response.json();

        if (response.ok && response.status === 200) {
          // Handle different response structures
          const rawShipmentsArray = data.data?.shipments || data.shipments || data.data || [];
          // Shipments created by Gmail automation sit in PENDING_APPROVAL/REJECTED until
          // a human approves them via the Pending Approval tab — keep them out of the
          // main list until then (manually-created records have approvalStatus === null).
          const shipmentsArray = Array.isArray(rawShipmentsArray)
            ? rawShipmentsArray.filter(s => s.approvalStatus !== 'PENDING_APPROVAL' && s.approvalStatus !== 'REJECTED')
            : rawShipmentsArray;

          if (Array.isArray(shipmentsArray)) {
            const formattedShipments = shipmentsArray.map(formatAirRow);
            if (append) {
              setShipments(prev => {
                const existing = new Set(prev.map(s => s.awbNumber));
                return [...prev, ...formattedShipments.filter(s => !existing.has(s.awbNumber))];
              });
            } else {
              setShipments(formattedShipments);
            }
            airRawCount.current = append
              ? airRawCount.current + rawShipmentsArray.length
              : rawShipmentsArray.length;
            setAirHasMore(rawShipmentsArray.length === PAGE_SIZE);
          } else if (!append) {
            setShipments([]);
            setAirHasMore(false);
          }
        }
      } catch (error) {
        console.error('Error fetching shipments:', error);
        if (!append) { setShipments([]); setAirHasMore(false); }
      } finally {
        if (!append) setShipmentsLoading(false);
        else setAirLoadingMore(false);
      }

      // Fetch Sea Export shipments (only on initial load)
      if (!append) {
      setLoadingSeaExport(true);
      try {
        const response = await apiCall(`${API_ENDPOINTS.GET_SEA_EXPORT}?limit=${PAGE_SIZE}&offset=${seaOffset}`, {
          method: 'GET'
        });

        const data = await response.json();
        console.log('Sea Export API Response (SuperAdmin):', data);

        if (response.ok && data.success) {
          const formattedShipments = (data.data || []).map(formatSeaRow);
          
          setSeaExportShipments(formattedShipments);
          setSeaHasMore(formattedShipments.length === PAGE_SIZE);
        }
      } catch (error) {
        console.error('Error fetching sea export shipments:', error);
      } finally {
        setLoadingSeaExport(false);
      }
      } // end if (!append)
    };

    fetchShipmentsRef.current = fetchAllShipments;

    if (activeTab === 'shipments') {
      fetchAllShipments();
      // The invoice picker on this tab bills any client, so it needs the user
      // list even when Users Management has not been opened.
      if (globalUsers.length === 0 && allUsers.length === 0) fetchUsers();
    } else if (activeTab === 'users' && globalUsers.length === 0 && allUsers.length === 0) {
      fetchUsers();
    } else if (activeTab === 'wallets') {
      fetchWallets();
      if (globalUsers.length === 0 && allUsers.length === 0) fetchUsers();
    }
  }, [activeTab, globalUsers.length, allUsers.length]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const airObs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && airHasMore && !airLoadingMore) {
        setAirLoadingMore(true);
        if (fetchShipmentsRef.current) {
          fetchShipmentsRef.current(airRawCount.current, 0, true).finally(() => setAirLoadingMore(false));
        }
      }
    }, { threshold: 0.1 });
    if (airSentinelRef.current) airObs.observe(airSentinelRef.current);
    return () => airObs.disconnect();
  }, [airHasMore, airLoadingMore, shipments.length]);

  useEffect(() => {
    const seaObs = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && seaHasMore && !seaLoadingMore) {
        setSeaLoadingMore(true);
        const offset = seaExportShipments.length;
        apiCall(`${API_ENDPOINTS.GET_SEA_EXPORT}?limit=${PAGE_SIZE}&offset=${offset}`, { method: 'GET' })
          .then(r => r.json())
          .then(data => {
            if (data.success) {
              const more = (data.data || []).map(s => ({
                id: s.id,
                portCode: s.port_code || s.portCode || '-',
                sbNo: s.sb_no || s.sbNo || '-',
                sbDate: s.sb_date || s.sbDate || '-',
                pkg: s.pkg || '-',
                gwt: s.gwt || '-',
                portOfLoading: s.port_of_loading || s.portOfLoading || '-',
                stateOfOrigin: s.state_of_origin || s.stateOfOrigin || '-',
                portOfDischargeCode: s.port_of_discharge_code || s.portOfDischargeCode || '-',
                portOfDischarge: s.port_of_discharge || s.portOfDischarge || '-',
                portOfFinalDestinationCode: s.port_of_final_destination_code || s.portOfFinalDestinationCode || '-',
                portOfFinalDestination: s.port_of_final_destination || s.portOfFinalDestination || '-',
                countryOfFinalDestination: s.country_of_final_destination || s.countryOfFinalDestination || '-',
                exporterName: s.exporter_name || s.exporterName || '-',
                buyerName: s.buyer_name || s.buyerName || '-',
                consigneeName: s.consignee_name || s.consigneeName || '-',
                mode: s.mode || '-',
                lut: s.lut || '-',
                fobValue: s.fob_value || s.fobValue || '-',
                dbkClaim: s.dbk_claim || s.dbkClaim || '-',
                igstAmt: s.igst_amt || s.igstAmt || '-',
                cessAmt: s.cess_amt || s.cessAmt || '-',
                igstValue: s.igst_value || s.igstValue || '-',
                rodtepAmt: s.rodtep_amt || s.rodtepAmt || '-',
                rosctlAmt: s.rosctl_amt || s.rosctlAmt || '-',
                invNo: s.inv_no || s.invNo || '-',
                invAmt: s.inv_amt || s.invAmt || '-',
                currency: s.currency || '-',
                leoDate: s.leo_date || s.leoDate || '-',
                invoiceNoDt: s.invoice_no_dt || s.invoiceNoDt || '-',
                blNumber: s.bl_number || s.blNumber || '-',
                containerNo: s.container_no || s.containerNo || '-',
                containerQty: s.container_qty || s.containerQty || '-',
                leoPdf: s.leo_pdf || s.leoPdf || '',
                blPdf: s.bl_pdf || s.blPdf || '',
                invPlPdf: s.inv_pl_pdf || s.invPlPdf || '',
                invoicePdf: s.invoice_pdf || s.invoicePdf || '',
                purPdf: s.pur_pdf || s.purPdf || '',
                admin_name: s.admin_name || s.adminName || '-',
                edited_by: s.edited_by || s.editedBy || '-',
                editedBy: s.edited_by || s.editedBy || '-',
                createdAt: s.created_at || s.createdAt || new Date().toISOString(),
                type: 'Sea-Export'
              }));
              setSeaExportShipments(prev => {
                const existing = new Set(prev.map(x => x.blNumber));
                return [...prev, ...more.filter(x => !existing.has(x.blNumber))];
              });
              setSeaHasMore(more.length === PAGE_SIZE);
            }
          })
          .catch(() => {})
          .finally(() => setSeaLoadingMore(false));
      }
    }, { threshold: 0.1 });
    if (seaSentinelRef.current) seaObs.observe(seaSentinelRef.current);
    return () => seaObs.disconnect();
  }, [seaHasMore, seaLoadingMore, seaExportShipments.length]);

  const handleRefreshUsers = async () => {
    setLoading(true);
    setError('');
    await fetchUsers();
    setLoading(false);
  };

  const generateStrongPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setEditUser({ ...editUser, password });
    setErrors({ ...errors, password: '' });
  };

  const validateForm = (user, isEdit = false) => {
    const newErrors = {};
    const usersToCheck = globalUsers.length > 0 ? globalUsers : allUsers;
    
    // Name validation
    if (!user.customerName || user.customerName.trim().length < 2) {
      newErrors.customerName = 'Customer name must be at least 2 characters';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!user.email || !emailRegex.test(user.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (isEdit && originalUser) {
      // Only check for duplicates if email has changed
      if (user.email.toLowerCase() !== originalUser.email?.toLowerCase()) {
        const emailExists = usersToCheck.some(u => 
          u.email?.toLowerCase() === user.email.toLowerCase() && u.id !== selectedUser?.id
        );
        if (emailExists) {
          newErrors.email = 'This email is already registered';
        }
      }
    } else if (!isEdit) {
      // For new users, always check
      const emailExists = usersToCheck.some(u => 
        u.email?.toLowerCase() === user.email.toLowerCase()
      );
      if (emailExists) {
        newErrors.email = 'This email is already registered';
      }
    }
    
    // Phone validation
    const phoneRegex = /^[0-9]{10}$/;
    if (!user.phoneNumber || !phoneRegex.test(user.phoneNumber)) {
      newErrors.phoneNumber = 'Phone number must be exactly 10 digits';
    } else if (isEdit && originalUser) {
      // Only check for duplicates if phone has changed
      if (user.phoneNumber !== originalUser.phoneNumber) {
        const phoneExists = usersToCheck.some(u => 
          u.mobile === user.phoneNumber && u.id !== selectedUser?.id
        );
        if (phoneExists) {
          newErrors.phoneNumber = 'This phone number is already registered';
        }
      }
    } else if (!isEdit) {
      // For new users, always check
      const phoneExists = usersToCheck.some(u => 
        u.mobile === user.phoneNumber
      );
      if (phoneExists) {
        newErrors.phoneNumber = 'This phone number is already registered';
      }
    }
    
    // Username validation
    if (!user.username || user.username.trim().length < 5) {
      newErrors.username = 'Username must be at least 5 characters';
    } else if (isEdit && originalUser) {
      // Only check for duplicates if username has changed
      if (user.username.toLowerCase() !== originalUser.username?.toLowerCase()) {
        const usernameExists = usersToCheck.some(u => 
          u.username?.toLowerCase() === user.username.toLowerCase() && u.id !== selectedUser?.id
        );
        if (usernameExists) {
          newErrors.username = 'This username is already taken';
        }
      }
    } else if (!isEdit) {
      // For new users, always check
      const usernameExists = usersToCheck.some(u => 
        u.username?.toLowerCase() === user.username.toLowerCase()
      );
      if (usernameExists) {
        newErrors.username = 'This username is already taken';
      }
    }
    
    // Password validation (only for edit if password provided)
    if (isEdit) {
      if (user.password && user.password.length > 0 && user.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    } else {
      if (!user.password || user.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    }
    
    // Website validation (optional)
    if (user.website && user.website.trim()) {
      const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!urlRegex.test(user.website)) {
        newErrors.website = 'Please enter a valid website URL';
      }
    }

    // Admin accounts must carry at least one PDF-fetch email
    if (user.role === 'admin' && (!user.emailToFetchPdf || user.emailToFetchPdf.length === 0)) {
      newErrors.emailToFetchPdf = 'Add at least one email';
    }

    return newErrors;
  };

  const handleEditUserClick = (user) => {
    console.log('Edit User Clicked - User object:', user);
    setSelectedUser(user);
    const phoneNum = normalizePhoneNumber(user.mobile || user.phoneNumber);
    const emailToFetchPdf = pickEmailToFetchPdf(user);
    const userData = {
      customerName: user.name,
      email: user.email,
      phoneNumber: phoneNum,
      username: user.username,
      password: user.password || '',
      role: user.role,
      status: user.status || 'active',
      website: user.website || '',
      emailToFetchPdf
    };
    console.log('Edit User - userData:', userData);
    setEditUser(userData);
    // Store original values for comparison
    const originalData = {
      customerName: user.name,
      email: user.email,
      phoneNumber: phoneNum,
      username: user.username,
      role: user.role,
      status: user.status || 'active',
      website: user.website || '',
      emailToFetchPdf
    };
    console.log('Edit User - originalData:', originalData);
    setOriginalUser(originalData);
    setErrors({});
    setEditApiError('');
    setShowPassword(false);
    setShowEditUserModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setErrors({});
    setEditApiError('');
    
    console.log('Update User - Current editUser state:', editUser);
    console.log('Update User - Original user state:', originalUser);
    
    const validationErrors = validateForm(editUser, true);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setLoading(true);
    
    try {
      // Build object with only changed fields
      const changedFields = {};
      
      if (editUser.username !== originalUser.username) {
        changedFields.username = editUser.username;
        console.log('Username changed:', editUser.username);
      }
      if (editUser.email !== originalUser.email) {
        changedFields.email = editUser.email;
        console.log('Email changed:', editUser.email);
      }
      if (editUser.customerName !== originalUser.customerName) {
        changedFields.Name = editUser.customerName;
        console.log('Customer name changed:', editUser.customerName);
      }
      if (editUser.phoneNumber !== originalUser.phoneNumber) {
        changedFields.phNum = `+91${editUser.phoneNumber}`;
        console.log('Phone number changed:', editUser.phoneNumber, ' -> Sending as:', changedFields.phNum);
      }
      if (editUser.role !== originalUser.role) {
        changedFields.role = editUser.role;
        console.log('Role changed:', editUser.role);
      }
      if (editUser.status !== originalUser.status) {
        changedFields.status = editUser.status;
        console.log('Status changed:', editUser.status);
      }
      if (editUser.website !== originalUser.website) {
        changedFields.website = editUser.website || '';
        console.log('Website changed:', editUser.website);
      }
      if (
        editUser.role === 'admin' &&
        JSON.stringify(editUser.emailToFetchPdf) !== JSON.stringify(originalUser.emailToFetchPdf)
      ) {
        changedFields.emailToFetchPdf = editUser.emailToFetchPdf;
        console.log('Email to fetch PDF changed:', editUser.emailToFetchPdf);
      }
      // Include password if it was changed (not empty)
      if (editUser.password && editUser.password.trim() !== '') {
        changedFields.password = await hashPassword(editUser.password);
        console.log('Password changed');
      }
      
      console.log('Changed fields to send to API:', changedFields);
      
      // If no fields changed, just close modal
      if (Object.keys(changedFields).length === 0) {
        console.log('No fields changed, closing modal');
        closeModals();
        return;
      }
      
      const response = await apiCall(`${API_ENDPOINTS.UPDATE_USER}${selectedUser.id}`, {
        method: 'PATCH',
        body: JSON.stringify(changedFields)
      });

      const data = await response.json().catch(() => ({}));
      console.log('Update user response:', response.status, data);

      // The API can report a failure with a 200 body (success:false), so
      // don't trust the status code alone.
      const failed = !response.ok || data?.success === false || data?.status === 'error';

      if (!failed) {
        // Update local state
        const updatedUsers = allUsers.map(user => 
          user.id === selectedUser.id 
            ? { 
                ...user, 
                name: editUser.customerName,
                email: editUser.email,
                mobile: editUser.phoneNumber,
                username: editUser.username,
                password: editUser.password || user.password,
                role: editUser.role,
                status: editUser.status,
                website: editUser.website || null,
                emailToFetchPdf: editUser.emailToFetchPdf
              }
            : user
        );
        setAllUsers(updatedUsers);
        closeModals();
      } else {
        const message = extractApiErrorMessage(data, 'Failed to update user');
        if (isEmailToFetchPdfError(data, message, editUser.emailToFetchPdf)) {
          setErrors((prev) => ({ ...prev, emailToFetchPdf: message }));
        } else {
          setEditApiError(message);
        }
      }
    } catch (err) {
      console.error('Update user error:', err);
      setEditApiError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiCall(`${API_ENDPOINTS.DELETE_USER}${selectedUser.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (response.ok && response.status === 200) {
        // Remove user from local state
        const updatedUsers = allUsers.filter(user => user.id !== selectedUser.id);
        setAllUsers(updatedUsers);
        setShowDeleteConfirm(false);
        setSelectedUser(null);
      } else {
        setError(data.message || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Delete user error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const closeModals = () => {
    setShowEditUserModal(false);
    setShowDeleteConfirm(false);
    setSelectedUser(null);
    setOriginalUser(null);
    setErrors({});
    setEditApiError('');
    setShowPassword(false);
    setEditUser({
      customerName: '',
      email: '',
      phoneNumber: '',
      username: '',
      password: '',
      role: 'employee',
      status: 'active',
      website: '',
      emailToFetchPdf: []
    });
  };

  // Both invoice kinds need the full list: the table to decide Generate vs
  // Revise, and the form to continue the HMR number series.
  const fetchAllInvoices = useCallback(async () => {
    try {
      const [airData, seaData] = await Promise.all([
        apiCall(API_ENDPOINTS.INVOICE_SUBMIT, { method: 'GET' })
          .then((r) => (r.ok ? r.json() : null)).catch(() => null),
        apiCall(API_ENDPOINTS.INVOICE_SUBMIT_SEA, { method: 'GET' })
          .then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      const map = {};
      const flat = [];
      [airData, seaData].forEach((d) => {
        if (!d) return;
        const list = Array.isArray(d) ? d : (d.data || d.invoices || []);
        list.forEach((inv) => {
          flat.push(inv);
          const no = inv.awbNumber || inv.awb_number || inv.blNumber || inv.bl_number;
          if (no) (map[no] = map[no] || []).push(inv);
        });
      });
      setInvoiceMap(map);
      setAllInvoicesList(flat);
    } catch (err) {
      console.error('Super admin: could not load invoices', err);
    }
  }, []);

  useEffect(() => { fetchAllInvoices(); }, [fetchAllInvoices]);

  // The table pages in as you scroll, so counting its rows made the figures
  // climb. They are read once from the whole book instead.
  useEffect(() => {
    if (activeTab !== 'shipments' || shipBook) return undefined;
    let cancelled = false;

    (async () => {
      const [airRes, seaRes] = await Promise.all([
        apiCall(API_ENDPOINTS.GET_ALL_SHIPMENTS, { method: 'GET' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
        apiCall(API_ENDPOINTS.GET_SEA_EXPORT, { method: 'GET' }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      ]);
      if (cancelled) return;

      const airRaw = (airRes?.data?.shipments || airRes?.shipments || airRes?.data || [])
        .filter((x) => x.approvalStatus !== 'PENDING_APPROVAL' && x.approvalStatus !== 'REJECTED');
      const seaRaw = seaRes?.data || seaRes?.shipments || [];

      // Mapped the same way the table maps its page, so filters, search and the
      // figures all read one shape.
      setShipBook({
        air: Array.isArray(airRaw) ? airRaw.map(formatAirRow) : [],
        sea: Array.isArray(seaRaw) ? seaRaw.map(formatSeaRow) : [],
      });
    })();

    return () => { cancelled = true; };
  }, [activeTab, shipBook]);

  const handleGenerateInvoice = (shipment) => {
    setInvoiceShipment({ ...shipment, existingInvoice: null });
    setShowInvoiceGeneration(true);
  };

  const handleEditInvoice = (shipment, existingInvoice) => {
    setInvoiceShipment({ ...shipment, existingInvoice });
    setShowInvoiceGeneration(true);
  };

  // A customer invoice carries no shipment, so it opens on its own.
  if (customerInvoiceOpen) {
    return (
      <InvoiceGenerationPage
        customerInvoice
        /* A super admin bills any client in the system, so the picker walks every
           customer account rather than only the ones under this login. */
        customerUsers={(() => {
          const everyone = globalUsers.length > 0 ? globalUsers : allUsers;
          const clients = everyone.filter((u) => !u.role || u.role === 'user' || u.role === 'customer');
          return clients.length ? clients : customers;
        })()}
        existingInvoice={customerInvoiceEdit}
        allInvoices={allInvoicesList}
        onClose={() => { setCustomerInvoiceOpen(false); setCustomerInvoiceEdit(null); fetchAllInvoices(); }}
      />
    );
  }

  if (showInvoiceGeneration && invoiceShipment) {
    return (
      <InvoiceGenerationPage
        shipment={invoiceShipment}
        existingInvoice={invoiceShipment?.existingInvoice || null}
        allInvoices={allInvoicesList}
        onClose={() => {
          setShowInvoiceGeneration(false);
          setInvoiceShipment(null);
          fetchAllInvoices();
        }}
      />
    );
  }

  // The one box in the topbar searches whatever tab is open, and writes to the
  // same state that tab's own box uses — so the two mirror each other rather
  // than disagreeing. A tab with nothing to search gets no box at all.
  const tabSearch = {
    users:      { value: consoleSearch, set: setConsoleSearch, hint: 'Search user, email, phone or ID…' },
    shipments:  { value: shipSearch,    set: setShipSearch,
                  hint: shipmentType === 'Air-Export'
                    ? 'Search AWB, S/B, client, consignee…'
                    : 'Search B/L, S/B, exporter, port…' },
    wallets:    { value: walletSearch,  set: setWalletSearch, hint: 'Search wallet by user or email…' },
    clients:    { value: clientSearch,  set: setClientSearch, hint: 'Search client, GSTIN or address…' },
    hsn:        { value: hsnSearch,     set: setHsnSearch,    hint: 'Search HSC code or service…' },
  }[activeTab] || null;

  const navSections = [
    { title: 'Super admin core', items: [
      { id: 'users',           label: 'Users Management',  icon: Users },
      { id: 'shipments',       label: 'All Shipments',     icon: Package },
      { id: 'wallets',         label: 'Wallet Management', icon: Wallet },
      { id: 'pendingApproval', label: 'Pending Approval',  icon: Inbox, count: pendingApprovalCount },
      { id: 'clients',         label: 'Client Master',     icon: FileText },
      { id: 'hsn',             label: 'HSC Codes',         icon: Hash },
    ] },
  ];

  return (
    <AdminShell
      sections={navSections}
      activeId={activeTab}
      onSelect={setActiveTab}
      role="Super Admin"
      cta={{ label: 'Add New User', icon: UserPlus, id: 'addUser', onClick: () => setActiveTab('addUser') }}
      search={tabSearch?.value ?? ''}
      onSearch={tabSearch?.set}
      searchPlaceholder={tabSearch?.hint}
    >
      <div className="max-w-[1600px] mx-auto">
        {activeTab === 'hsn' && <HsnCodeManager search={hsnSearch} onSearch={setHsnSearch} />}

        {activeTab === 'clients' && (
          <ClientMaster
            role="super_admin"
            search={clientSearch}
            onSearch={setClientSearch}
            customers={customers}
            onEditInvoice={(invoice) => { setCustomerInvoiceEdit(invoice); setCustomerInvoiceOpen(true); }}
          />
        )}

        {activeTab === 'addUser' && (
          <AddUserPage
            allUsers={allUsers}
            embedded
            onDone={() => { setActiveTab('users'); handleRefreshUsers(); }}
          />
        )}

        {activeTab === 'users' && (
          <SuperAdminUsers
            users={allUsers.length > 0 ? allUsers : globalUsers}
            currentUser={currentUser}
            loading={loading}
            error={error}
            search={consoleSearch}
            onRetry={handleRefreshUsers}
            onAdd={() => setActiveTab('addUser')}
            onEdit={handleEditUserClick}
            onDelete={handleDeleteUser}
          />
        )}

        {/* ── All Shipments ─────────────────────────────────────────────── */}
        {activeTab === 'shipments' && (() => {
          const isAir = shipmentType === 'Air-Export';

          // dd/mm/yyyy, "20-JAN-2026" and ISO all turn up in these records.
          const asDate = (value) => {
            if (!value) return null;
            const raw = Array.isArray(value) ? value[0] : value;
            const str = String(raw).trim();
            const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
            if (dmy) return new Date(+dmy[3], +dmy[2] - 1, +dmy[1]);
            const d = new Date(str);
            return Number.isNaN(d.getTime()) ? null : d;
          };
          const shipDate = (s) => asDate(s.sbDate || s.sbDates || s.createdAt || s.created_at);
          const destOf = (s) => (isAir
            ? (s.destCountry || s.destCity || s.destCode || '')
            : (s.countryOfFinalDestination || s.portOfDischargeCode || ''));

          const inRange = (s) => {
            if (!shipFrom && !shipTo) return true;
            const d = shipDate(s);
            if (!d) return false;
            if (shipFrom && d < new Date(`${shipFrom}T00:00:00`)) return false;
            if (shipTo && d > new Date(`${shipTo}T23:59:59`)) return false;
            return true;
          };
          const hitsSearch = (s) => matchesShipmentSearch(s, shipSearch);
          const hitsDest = (s) => !shipDests.length || shipDests.some((d) => normalizeValue(d) === normalizeValue(destOf(s)));
          const matchesPayment = makePaymentMatcher({ invoiceMap, paymentFilter, unpaidSince, overdueDays });
          const hitsPayment = (s) => matchesPayment(s.awbNumber || s.blNumber || s.sbNo, !!s.invoice);
          // A card can narrow the table as well as read it.
          const billed = (s) => (invoiceMap[s.awbNumber] || invoiceMap[s.blNumber] || []).length > 0;
          const hitsKpi = (s) => (kpiFilter === 'unbilled' ? !billed(s)
            : kpiFilter === 'billed' ? billed(s)
            : true);

          // A filter has to search the whole book — the match may sit on a page
          // the table has not reached yet.
          const anyFilter = !!(selectedCustomer.length || selectedAdmin.length || shipSearch.trim()
            || shipDests.length || shipFrom || shipTo || kpiFilter !== 'all'
            || paymentFilter !== 'all' || unpaidSince || overdueDays !== '');
          const airSource = anyFilter ? (shipBook?.air || shipments) : shipments;
          const seaSource = anyFilter ? (shipBook?.sea || seaExportShipments) : seaExportShipments;

          const airRows = airSource.filter((s) => {
            const customerMatch = !selectedCustomer.length || selectedCustomer.some((c) => normalizeValue(s.customerName) === normalizeValue(c));
            const adminMatch = !selectedAdmin.length || selectedAdmin.some((a) => normalizeValue(s.adminName) === normalizeValue(a));
            return customerMatch && adminMatch && hitsSearch(s) && hitsDest(s) && inRange(s) && hitsKpi(s) && hitsPayment(s);
          });
          const seaRows = seaSource.filter((s) => {
            const customerMatch = !selectedCustomer.length || selectedCustomer.some((c) => normalizeValue(s.exporterName) === normalizeValue(c));
            const adminMatch = !selectedAdmin.length || selectedAdmin.some((a) => normalizeValue(s.admin_name) === normalizeValue(a));
            return customerMatch && adminMatch && hitsSearch(s) && hitsDest(s) && inRange(s) && hitsKpi(s) && hitsPayment(s);
          });
          const rows = isAir ? airRows : seaRows;

          const total = (shipBook?.air || shipments).length + (shipBook?.sea || seaExportShipments).length;
          const share = (n) => (total ? `${((n / total) * 100).toFixed(1)}%` : '—');

          const destinations = [...new Set((shipBook?.[isAir ? 'air' : 'sea'] || (isAir ? shipments : seaExportShipments)).map(destOf).filter((v) => v && v !== '-'))].sort();
          const pretty = (iso) => (iso ? iso.split('-').reverse().join('/') : '');
          const dateLabel = !shipFrom && !shipTo
            ? 'All dates'
            : (shipFrom === shipTo || !shipTo)
              ? pretty(shipFrom || shipTo)
              : `${pretty(shipFrom) || '…'} – ${pretty(shipTo) || '…'}`;

          const facets = [
            ...selectedCustomer.map((name) => ({ key: `c-${name}`, label: `Client: ${name}`, clear: () => toggleCustomerSelection(name) })),
            ...selectedAdmin.map((name) => ({ key: `a-${name}`, label: `Admin: ${name}`, clear: () => toggleAdminSelection(name) })),
            ...shipDests.map((d) => ({ key: `d-${d}`, label: `Destination: ${d}`, clear: () => setShipDests((prev) => prev.filter((x) => x !== d)) })),
            ...(shipSearch.trim() ? [{ key: 'q', label: `Search: ${shipSearch.trim()}`, clear: () => setShipSearch('') }] : []),
            ...((shipFrom || shipTo) ? [{ key: 'date', label: `Dates: ${dateLabel}`, clear: () => { setShipFrom(''); setShipTo(''); } }] : []),
            ...(kpiFilter !== 'all' ? [{ key: 'kpi', label: kpiFilter === 'billed' ? 'Invoiced' : 'Awaiting invoice', clear: () => setKpiFilter('all') }] : []),
            ...(paymentFilter !== 'all' ? [{ key: 'pay', label: `Billing: ${paymentFilter.replace(/_/g, ' ')}`, clear: () => setPaymentFilter('all') }] : []),
            ...(unpaidSince ? [{ key: 'since', label: `Unpaid since ${unpaidSince.toLocaleDateString('en-GB')}`, clear: () => setUnpaidSince(null) }] : []),
            ...(overdueDays !== '' ? [{ key: 'over', label: `Overdue by ${overdueDays}d`, clear: () => setOverdueDays('') }] : []),
          ];
          const resetAll = () => {
            setSelectedCustomer([]); setSelectedAdmin([]);
            setShipSearch(''); setShipDests([]); setShipFrom(''); setShipTo(''); setKpiFilter('all');
            setPaymentFilter('all'); setUnpaidSince(null); setOverdueDays('');
          };

          // The figures describe whichever book is on screen, so switching the
          // mode re-reads them rather than restating the same four totals.
          const loaded = shipBook?.[isAir ? 'air' : 'sea'] || (isAir ? shipments : seaExportShipments);

          // The rows carry FOB as the joined display string ("1,000 & 2,000"),
          // so every part is summed rather than parsed as one number.
          const fobOf = (x) => String((isAir ? x.fob : x.fobValue) ?? '')
            .split('&')
            .reduce((sum, part) => sum + (parseFloat(part.replace(/[^\d.-]/g, '')) || 0), 0);
          // Whole-book figures once they land; what is loaded until then.
          const book = {
            count: loaded.length,
            invoiced: loaded.filter(billed).length,
            awaiting: loaded.length - loaded.filter(billed).length,
            fob: loaded.reduce((sum, x) => sum + fobOf(x), 0),
          };
          const modeBilled = book.invoiced;
          const modeUnbilled = book.awaiting;
          const modeFob = book.fob;
          const pct = (n) => (book.count ? `${Math.round((n / book.count) * 100)}%` : '—');
          const shortInr = (n) => (n >= 1e7 ? `₹${(n / 1e7).toFixed(2)} Cr`
            : n >= 1e5 ? `₹${(n / 1e5).toFixed(2)} L`
            : `₹${Math.round(n).toLocaleString('en-IN')}`);

          const miniStats = [
            {
              id: 'total',
              label: isAir ? 'Air consignments' : 'Sea consignments',
              value: book.count,
              note: `${share(book.count)} of the book`,
              bar: isAir ? 'bg-cyan-500' : 'bg-emerald-500',
              tone: isAir ? 'text-cyan-600 dark:text-cyan-300' : 'text-emerald-600 dark:text-emerald-300',
              on: kpiFilter === 'all',
              apply: () => setKpiFilter('all'),
            },
            {
              id: 'billed', label: 'Invoiced', value: modeBilled, note: pct(modeBilled),
              bar: 'bg-blue-500', tone: 'text-blue-600 dark:text-blue-300',
              on: kpiFilter === 'billed',
              apply: () => setKpiFilter(kpiFilter === 'billed' ? 'all' : 'billed'),
            },
            {
              id: 'unbilled', label: 'Awaiting invoice', value: modeUnbilled, note: pct(modeUnbilled),
              bar: 'bg-amber-500', tone: 'text-amber-600 dark:text-amber-300',
              on: kpiFilter === 'unbilled',
              apply: () => setKpiFilter(kpiFilter === 'unbilled' ? 'all' : 'unbilled'),
            },
            {
              id: 'fob', label: 'FOB value', value: shortInr(modeFob),
              note: `across ${book.count} ${isAir ? 'AWBs' : 'B/Ls'}`,
              bar: 'bg-violet-500', tone: 'text-violet-600 dark:text-violet-300',
              on: false, apply: null,
            },
          ];

          return (
            <div>
              <PageHead
                eyebrow="Super admin core · Consignments"
                title="All Shipments"
                badge={<Pill tone="emerald" dot>{total} active consignments</Pill>}
                subtitle="Every consignment across all client tenants, customs milestones and carrier networks."
                actions={(
                  <>
                    <button type="button" className={btn.quiet} onClick={() => setCustomerInvoiceListOpen(true)}>
                      <Receipt size={16} /> Customer invoices
                    </button>
                    <button type="button" className={btn.primary} onClick={() => setCustomerInvoiceOpen(true)}>
                      <FileText size={16} /> Generate invoice
                    </button>
                    <button type="button" className={btn.green}
                      onClick={() => exportToCSV(rows, `shipments_${new Date().toISOString().split('T')[0]}.csv`)}>
                      <Download size={16} /> Export CSV
                    </button>
                    <button type="button" className={`${btn.quiet} w-10 px-0 justify-center`} title="Reload shipments"
                      onClick={() => fetchShipmentsRef.current?.(0, 0, false)}>
                      <RotateCw size={16} />
                    </button>
                  </>
                )}
              />

              {/* mode + the counted figures, one band */}
              <Panel className="px-4 sm:px-5 py-4 mb-5">
                <div className="flex flex-col xl:flex-row xl:items-center gap-4">
                  <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800/70 self-start">
                    {[
                      { id: 'Air-Export', label: 'Air Export', icon: Plane, count: (shipBook?.air || shipments).length },
                      { id: 'Sea-Export', label: 'Sea Export', icon: Ship, count: (shipBook?.sea || seaExportShipments).length },
                    ].map((t) => {
                      const Icon = t.icon;
                      const on = shipmentType === t.id;
                      return (
                        <button key={t.id} type="button" onClick={() => setShipmentType(t.id)}
                          className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg text-[13px] font-bold transition ${on
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:bg-white/70 dark:hover:bg-slate-700/60'}`}>
                          <Icon size={15} />
                          {t.label}
                          <span className={`px-1.5 rounded-md text-[11px] ${on ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700'}`}>{t.count}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
                    {miniStats.map((m) => (
                      <button key={m.id} type="button" onClick={m.apply || undefined} disabled={!m.apply}
                        title={m.apply ? `Show ${m.label.toLowerCase()}` : undefined}
                        className={`relative overflow-hidden rounded-xl border px-3.5 pt-2.5 pb-3 text-left transition
                                    ${!m.apply ? 'cursor-default ' : ''}${m.on
                                      ? 'border-blue-400 bg-blue-50 dark:border-blue-500/60 dark:bg-blue-500/10 ring-2 ring-blue-500/20'
                                      : 'border-slate-200 bg-slate-50/70 hover:bg-slate-100 dark:border-slate-800 dark:bg-slate-900/40 dark:hover:bg-slate-800/60'}`}>
                        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">{m.label}</p>
                        <div className={`mt-1 ${m.apply ? 'flex items-baseline gap-2' : ''}`}>
                          <span className={`leading-none font-black text-slate-900 dark:text-white
                                            ${m.apply ? 'text-[22px]' : 'text-[19px]'}`}>{m.value}</span>
                          <span className={`block text-[11px] font-bold ${m.tone} ${m.apply ? '' : 'mt-1'}`}>{m.note}</span>
                        </div>
                        <span className={`absolute left-0 right-0 bottom-0 h-[3px] ${m.bar}`} />
                      </button>
                    ))}
                  </div>
                </div>
              </Panel>

              <ShipmentFilters
                customers={getCustomerOptions()}
                selectedCustomers={selectedCustomer}
                onToggleCustomer={toggleCustomerSelection}
                onClearCustomers={() => setSelectedCustomer([])}
                admins={getAdminOptions()}
                selectedAdmins={selectedAdmin}
                onToggleAdmin={toggleAdminSelection}
                onClearAdmins={() => setSelectedAdmin([])}
                dateLabel={dateLabel}
                onOpenDates={() => setShowShipDates(true)}
                destinations={destinations}
                selectedDests={shipDests}
                onToggleDest={(d) => setShipDests((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]))}
                onClearDests={() => setShipDests([])}
                paymentFilter={paymentFilter}
                onPaymentFilter={setPaymentFilter}
                unpaidSince={unpaidSince}
                onUnpaidSince={setUnpaidSince}
                unpaidOpen={showUnpaidSincePicker}
                onUnpaidOpen={setShowUnpaidSincePicker}
                overdueDays={overdueDays}
                onOverdueDays={setOverdueDays}
                facets={facets}
                onReset={resetAll}
              />

              {showShipDates && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
                  onClick={() => setShowShipDates(false)}>
                  <div className="rounded-2xl shadow-2xl overflow-hidden max-w-fit max-h-[90vh] flex flex-col
                                  bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700"
                    onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between gap-6 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                      <div>
                        <p className="text-[14px] font-bold text-slate-900 dark:text-white">Shipment dates</p>
                        <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                          Pick a day for that day alone, or a start and an end for a range.
                        </p>
                      </div>
                      <button type="button" onClick={() => setShowShipDates(false)}
                        className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <X size={18} />
                      </button>
                    </div>

                    <div className="ql-daterange overflow-auto flex-1">
                      <DateRangePicker
                        ranges={[{
                          startDate: shipFrom ? new Date(`${shipFrom}T00:00:00`) : new Date(),
                          endDate: shipTo ? new Date(`${shipTo}T00:00:00`) : (shipFrom ? new Date(`${shipFrom}T00:00:00`) : new Date()),
                          key: 'selection',
                        }]}
                        onChange={({ selection }) => {
                          const iso = (d) => (d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0] : '');
                          setShipFrom(iso(selection.startDate));
                          setShipTo(iso(selection.endDate));
                        }}
                        inputRanges={[]}
                        months={window.innerWidth < 768 ? 1 : 2}
                        direction={window.innerWidth < 768 ? 'vertical' : 'horizontal'}
                        showSelectionPreview
                        moveRangeOnFirstSelection={false}
                        minDate={new Date(2020, 0, 1)}
                        rangeColors={['#2563eb']}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-t border-slate-100 dark:border-slate-800">
                      <button type="button" className={btn.quiet}
                        onClick={() => { setShipFrom(''); setShipTo(''); }}>
                        Clear dates
                      </button>
                      <button type="button" className={btn.primary} onClick={() => setShowShipDates(false)}>
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <Panel className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                  <span className="grid place-items-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
                    {isAir ? <Plane size={16} /> : <Ship size={16} />}
                  </span>
                  <p className="text-[14px] font-bold text-slate-900 dark:text-white">
                    {isAir ? 'Air export manifests' : 'Sea export manifests'}
                  </p>
                  <Pill tone="slate">{rows.length} rows shown</Pill>

                  <div className="ml-auto flex flex-wrap items-center gap-4">
                    {[
                      { dot: 'bg-emerald-500', label: 'Invoice raised' },
                      { dot: 'bg-amber-500', label: 'Awaiting invoice' },
                      { dot: 'bg-blue-500', label: 'Payment recorded' },
                    ].map((l) => (
                      <span key={l.label} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
                        <span className={`w-2 h-2 rounded-full ${l.dot}`} /> {l.label}
                      </span>
                    ))}
                  </div>
                </div>

                {isAir ? (
                  shipmentsLoading ? (
                    <div className="py-20 grid place-items-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                    </div>
                  ) : (
                    <>
                      <ShipmentsTable
                        flush
                        shipments={airRows}
                        userRole="super_admin"
                        onGenerateInvoice={handleGenerateInvoice}
                        onEditInvoice={handleEditInvoice}
                        invoiceMap={invoiceMap}
                      />
                      {!anyFilter && <div ref={airSentinelRef} className="h-8" />}
                      {airLoadingMore ? (
                        <div className="flex items-center justify-center gap-3 py-4 text-sm text-slate-500 dark:text-slate-400">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                          Loading more…
                        </div>
                      ) : (!anyFilter && airHasMore && (
                        <div className="flex justify-center py-4">
                          <button
                            type="button"
                            className={btn.quiet}
                            onClick={() => {
                              setAirLoadingMore(true);
                              fetchShipmentsRef.current?.(airRawCount.current, 0, true)
                                .finally(() => setAirLoadingMore(false));
                            }}
                          >
                            Load more shipments
                          </button>
                        </div>
                      ))}
                      {anyFilter ? (
                        <p className="text-center text-xs text-slate-400 py-3">
                          {airRows.length} of {(shipBook?.air || shipments).length} air export shipments match
                        </p>
                      ) : !airHasMore && shipments.length > 0 && !airLoadingMore && (
                        <p className="text-center text-xs text-slate-400 py-3">All {shipments.length} air export shipments loaded</p>
                      )}
                    </>
                  )
                ) : (
                  <>
                    <SeaExportTableReadOnly flush shipments={seaRows} loading={loadingSeaExport} userRole="super_admin" />
                    {!anyFilter && <div ref={seaSentinelRef} className="h-8" />}
                    {seaLoadingMore && (
                      <div className="flex items-center justify-center gap-3 py-4 text-sm text-slate-500 dark:text-slate-400">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                        Loading more…
                      </div>
                    )}
                    {anyFilter ? (
                      <p className="text-center text-xs text-slate-400 py-3">
                        {seaRows.length} of {(shipBook?.sea || seaExportShipments).length} sea export shipments match
                      </p>
                    ) : !seaHasMore && seaExportShipments.length > 0 && !seaLoadingMore && (
                      <p className="text-center text-xs text-slate-400 py-3">All {seaExportShipments.length} sea export shipments loaded</p>
                    )}
                  </>
                )}
              </Panel>
            </div>
          );
        })()}

        {/* Wallet Management Section */}
        {/* ── Wallet Management ─────────────────────────────────────────── */}
        {activeTab === 'wallets' && (() => {
          const usersArray = (globalUsers.length > 0 ? globalUsers : allUsers)
            .filter(u => u.role === 'user' || u.role === 'customer' || (!u.role || u.role === 'user'));
          const walletByUserId = {};
          wallets.forEach(w => { walletByUserId[w.customer_id ?? w.customerId] = w; });

          const q = walletSearch.trim().toLowerCase();
          const matches = (u) => !q
            || (u.name || '').toLowerCase().includes(q)
            || (u.username || '').toLowerCase().includes(q)
            || String(u.id).includes(q);

          const balanceOf = (u) => paiseToRupees(
            (walletByUserId[u.id]?.balance_paise) ?? (walletByUserId[u.id]?.balancePaise)
          );

          const provisioned = usersArray.filter((u) => walletByUserId[u.id]);
          const unprovisioned = usersArray.filter((u) => !walletByUserId[u.id]);

          const totalBalance = provisioned.reduce((sum, u) => sum + balanceOf(u), 0);
          const activeWallets = provisioned.filter((u) => walletByUserId[u.id].status === 'active').length;
          const blockedWallets = provisioned.length - activeWallets;
          const zeroBalance = provisioned.filter((u) => balanceOf(u) <= 0);

          const passesStatus = (u) => {
            const w = walletByUserId[u.id];
            if (walletStatusFilter === 'active') return w.status === 'active';
            if (walletStatusFilter === 'blocked') return w.status !== 'active';
            if (walletStatusFilter === 'zero') return balanceOf(u) <= 0;
            return true;
          };

          const withWallet = provisioned.filter((u) => matches(u) && passesStatus(u)).sort((a, b) => (
            walletSort === 'balance' ? balanceOf(b) - balanceOf(a)
              : walletSort === 'name' ? String(a.name || a.username || '').localeCompare(String(b.name || b.username || ''))
              : b.id - a.id
          ));
          const withoutWallet = unprovisioned
            .filter((u) => matches(u) && walletStatusFilter === 'all')
            .sort((a, b) => b.id - a.id);

          // Every transaction the open wallets have handed back, newest first.
          const ledger = Object.entries(walletTxns).flatMap(([walletId, list]) => {
            const owner = provisioned.find((u) => String(walletByUserId[u.id].id) === String(walletId));
            return (list || []).map((t) => ({ ...t, owner }));
          }).sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));

          const walletFilters = !!(q || walletStatusFilter !== 'all');

          return (
            <div>
              <PageHead
                eyebrow="Super admin core · Fiscal controller"
                title="Wallet Management"
                badge={<Pill tone="emerald" dot>{provisioned.length} provisioned</Pill>}
                subtitle="Prepaid balances, credit allocations and the ledger behind them."
                actions={(
                  <button type="button" className={btn.quiet}
                    onClick={() => { fetchWallets(); if (globalUsers.length === 0 && allUsers.length === 0) fetchUsers(); }}>
                    <RotateCw size={16} className={walletsLoading ? 'animate-spin' : ''} />
                    Sync balances
                  </button>
                )}
              />

              <StatRow>
                <StatCard label="Total balance" value={fmt(totalBalance)} icon={Wallet} tone="emerald"
                  note={`Across ${provisioned.length} wallet${provisioned.length === 1 ? '' : 's'}`} />
                <StatCard label="Active wallets" value={activeWallets} icon={CheckCircle2} tone="blue"
                  note={blockedWallets ? `${blockedWallets} blocked` : 'None blocked'} />
                <StatCard label="Zero balance" value={zeroBalance.length} icon={AlertTriangle} tone={zeroBalance.length ? 'rose' : 'slate'}
                  note={zeroBalance.length
                    ? `Needs top-up: ${zeroBalance.slice(0, 2).map((u) => `#${u.id}`).join(', ')}${zeroBalance.length > 2 ? '…' : ''}`
                    : 'Every wallet funded'} />
                <StatCard label="Unprovisioned" value={unprovisioned.length} icon={UserPlus} tone="amber"
                  note="Users with no wallet yet" />
              </StatRow>

              <Panel className="px-4 sm:px-5 py-4 mb-5">
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <Select
                    className="lg:w-52"
                    value={walletStatusFilter === 'all' ? '' : walletStatusFilter}
                    onChange={(v) => setWalletStatusFilter(v || 'all')}
                    options={[
                      { value: 'active', label: 'Active wallets' },
                      { value: 'blocked', label: 'Blocked wallets' },
                      { value: 'zero', label: 'Zero balance' },
                    ]}
                    allLabel="All wallets"
                  />

                  <Select
                    className="lg:w-52"
                    align="right"
                    value={walletSort === 'recent' ? '' : walletSort}
                    onChange={(v) => setWalletSort(v || 'recent')}
                    options={[
                      { value: 'balance', label: 'Sort: balance' },
                      { value: 'name', label: 'Sort: name' },
                    ]}
                    allLabel="Sort: newest"
                  />

                  {walletFilters && (
                    <button type="button" className={btn.quiet}
                      onClick={() => { setWalletSearch(''); setWalletStatusFilter('all'); }}>
                      <RotateCcw size={15} /> Reset
                    </button>
                  )}
                </div>
              </Panel>

              {walletsLoading ? (
                <Panel className="py-20 grid place-items-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </Panel>
              ) : (
                <div className="space-y-5">
                  {/* ── provisioned ── */}
                  <Panel className="overflow-hidden">
                    <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                      <span className="grid place-items-center w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                        <Wallet size={16} />
                      </span>
                      <p className="text-[14px] font-bold text-slate-900 dark:text-white">Wallets created</p>
                      <Pill tone="slate">{withWallet.length} shown</Pill>
                      <span className="ml-auto text-[12px] font-bold text-emerald-600 dark:text-emerald-400">{fmt(totalBalance)} held</span>
                    </div>

                    <div className="divide-y divide-slate-100 dark:divide-slate-800/80">
                      {withWallet.map((u) => {
                        const w = walletByUserId[u.id];
                        const balRupees = balanceOf(u);
                        const isExpanded = expandedWallet === w.id;
                        const txns = walletTxns[w.id] || [];
                        const txnLoading = walletTxnsLoading[w.id];
                        const displayName = u.name || u.username || `User #${u.id}`;
                        return (
                          <div key={u.id}>
                            <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 sm:px-5 py-4">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Avatar name={displayName} tone={w.status === 'active' ? 'emerald' : 'rose'} />
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-[14px] font-bold text-slate-900 dark:text-white truncate">{displayName}</p>
                                    <Pill tone={w.status === 'active' ? 'emerald' : 'rose'} dot>
                                      {w.status === 'active' ? 'Active' : 'Blocked'}
                                    </Pill>
                                    {balRupees <= 0 && <Pill tone="amber">Zero balance</Pill>}
                                  </div>
                                  <p className="text-[11.5px] text-slate-400 dark:text-slate-500 mt-0.5 font-mono">
                                    ID #{u.id}{u.username ? ` · @${u.username}` : ''} · Wallet #{w.id}
                                  </p>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 flex-wrap lg:justify-end">
                                <div className="text-right mr-1">
                                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">Available</p>
                                  <p className="text-[17px] font-black text-slate-900 dark:text-white leading-tight">{fmt(balRupees)}</p>
                                </div>
                                <button type="button" className={btn.green}
                                  onClick={() => setCreditModal({ walletId: w.id, userName: displayName, userEmail: u.email || '' })}>
                                  <PlusCircle size={15} /> Add Money
                                </button>
                                <button type="button"
                                  className={`inline-flex items-center gap-2 h-10 px-3.5 rounded-xl text-sm font-bold border transition ${
                                    w.status === 'active'
                                      ? 'border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-500/30 dark:text-rose-400 dark:hover:bg-rose-500/10'
                                      : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-300 dark:hover:bg-emerald-500/10'}`}
                                  onClick={() => handleToggleStatus(w.id, w.status)}>
                                  {w.status === 'active' ? <Lock size={15} /> : <Unlock size={15} />}
                                  {w.status === 'active' ? 'Block' : 'Unblock'}
                                </button>
                                <button type="button" className={btn.quiet}
                                  onClick={() => {
                                    if (isExpanded) setExpandedWallet(null);
                                    else { setExpandedWallet(w.id); fetchWalletTxns(w.id); }
                                  }}>
                                  <History size={15} /> History
                                  <ChevronDown size={14} className={isExpanded ? 'rotate-180 transition' : 'transition'} />
                                </button>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="px-4 sm:px-5 pb-4">
                                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 overflow-hidden">
                                  {txnLoading ? (
                                    <div className="py-10 grid place-items-center">
                                      <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
                                    </div>
                                  ) : txns.length === 0 ? (
                                    <p className="py-10 text-center text-[13px] text-slate-400">No transactions on this wallet yet.</p>
                                  ) : (
                                    <Table head={[
                                      { label: 'Date' }, { label: 'Type' }, { label: 'Method' },
                                      { label: 'Amount', align: 'right' }, { label: 'Remarks' },
                                    ]}>
                                      {txns.map((t) => {
                                        const isCredit = t.type === 'credit';
                                        const amount = paiseToRupees(t.amount_paise ?? t.amountPaise);
                                        return (
                                          <Row key={t.id}>
                                            <Cell className="whitespace-nowrap">
                                              {new Date(t.created_at || t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })}
                                            </Cell>
                                            <Cell>
                                              <Pill tone={isCredit ? 'emerald' : t.type === 'refund' ? 'blue' : 'rose'}>
                                                {String(t.type || '').toUpperCase()}
                                              </Pill>
                                            </Cell>
                                            <Cell className="capitalize">{t.source || '—'}</Cell>
                                            <Cell className={`text-right font-bold ${isCredit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
                                              {isCredit ? '+' : '−'}{fmt(amount)}
                                            </Cell>
                                            <Cell className="max-w-[220px] truncate">{t.remarks || '—'}</Cell>
                                          </Row>
                                        );
                                      })}
                                    </Table>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {withWallet.length === 0 && (
                        <p className="px-5 py-12 text-center text-[13px] text-slate-400">
                          {walletFilters ? 'No wallet matches these filters' : 'No wallets created yet'}
                        </p>
                      )}
                    </div>
                  </Panel>

                  {/* ── not provisioned ── */}
                  {withoutWallet.length > 0 && (
                    <Panel className="overflow-hidden">
                      <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <span className="grid place-items-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300">
                          <UserPlus size={16} />
                        </span>
                        <p className="text-[14px] font-bold text-slate-900 dark:text-white">No wallet yet</p>
                        <Pill tone="slate">{withoutWallet.length}</Pill>
                      </div>

                      <div className="divide-y divide-slate-100 dark:divide-slate-800/80">
                        {withoutWallet.map((u) => {
                          const displayName = u.name || u.username || `User #${u.id}`;
                          const isCreating = creatingWalletFor.has(u.id);
                          return (
                            <div key={u.id} className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5">
                              <div className="flex items-center gap-3 min-w-0">
                                <Avatar name={displayName} tone="slate" />
                                <div className="min-w-0">
                                  <p className="text-[13.5px] font-bold text-slate-900 dark:text-white truncate">{displayName}</p>
                                  <p className="text-[11.5px] text-slate-400 dark:text-slate-500 mt-0.5 font-mono">
                                    ID #{u.id}{u.username ? ` · @${u.username}` : ''}
                                  </p>
                                </div>
                              </div>
                              <button type="button" className={btn.primary} disabled={isCreating}
                                onClick={() => handleCreateWalletForUser(u.id, displayName)}>
                                {isCreating ? <RotateCw size={15} className="animate-spin" /> : <Wallet size={15} />}
                                {isCreating ? 'Creating…' : 'Create Wallet'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </Panel>
                  )}

                  {/* ── ledger ── */}
                  {ledger.length > 0 && (
                    <Panel className="overflow-hidden">
                      <div className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3.5 border-b border-slate-100 dark:border-slate-800">
                        <span className="grid place-items-center w-8 h-8 rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
                          <History size={16} />
                        </span>
                        <div>
                          <p className="text-[14px] font-bold text-slate-900 dark:text-white">Ledger activity</p>
                          <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                            Credits, deductions and refunds across the wallets opened above
                          </p>
                        </div>
                      </div>
                      <Table head={[
                        { label: 'Ref' }, { label: 'Tenant account' }, { label: 'Transaction type' },
                        { label: 'Method' }, { label: 'Amount', align: 'right' }, { label: 'When' },
                      ]}>
                        {ledger.slice(0, 25).map((t) => {
                          const isCredit = t.type === 'credit';
                          const amount = paiseToRupees(t.amount_paise ?? t.amountPaise);
                          return (
                            <Row key={`${t.id}-${t.owner?.id}`}>
                              <Cell className="font-mono text-[12px] text-blue-600 dark:text-blue-400 whitespace-nowrap">TXN-{t.id}</Cell>
                              <Cell className="whitespace-nowrap font-semibold text-slate-900 dark:text-white">
                                {t.owner ? (t.owner.name || t.owner.username) : '—'}
                                {t.owner && <span className="text-slate-400 font-normal"> (#{t.owner.id})</span>}
                              </Cell>
                              <Cell>
                                <Pill tone={isCredit ? 'emerald' : t.type === 'refund' ? 'blue' : 'violet'}>
                                  {String(t.type || '').toUpperCase()}
                                </Pill>
                              </Cell>
                              <Cell className="capitalize">{t.source || '—'}</Cell>
                              <Cell className={`text-right font-bold ${isCredit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400'}`}>
                                {isCredit ? '+' : '−'}{fmt(amount)}
                              </Cell>
                              <Cell className="whitespace-nowrap text-slate-500 dark:text-slate-400">
                                {new Date(t.created_at || t.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              </Cell>
                            </Row>
                          );
                        })}
                      </Table>
                    </Panel>
                  )}

                  {provisioned.length === 0 && unprovisioned.length === 0 && (
                    <Panel className="py-16 text-center">
                      <Users size={36} className="mx-auto mb-3 text-slate-300 dark:text-slate-700" />
                      <p className="text-[13px] text-slate-400">{walletSearch ? 'No users match your search.' : 'No users found.'}</p>
                    </Panel>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {activeTab === 'pendingApproval' && (
          <PendingApprovalQueue
            eyebrow="Super admin core · Automation"
            onApproved={() => fetchShipmentsRef.current?.(0, 0, false)}
            onCountChange={setPendingApprovalCount}
          />
        )}

      </div>

      {/* Credit Wallet Modal */}
      {creditModal && (() => {
        const wallet = wallets.find((w) => w.id === creditModal.walletId);
        const current = paiseToRupees(wallet?.balance_paise ?? wallet?.balancePaise);
        const adding = parseFloat(creditAmount) || 0;
        return (
          <Modal
            open
            onClose={() => setCreditModal(null)}
            icon={PlusCircle}
            tone="emerald"
            title="Add money"
            subtitle={`${creditModal.userName}${wallet ? ` · Wallet #${wallet.id}` : ''}`}
            footer={(
              <div className="flex flex-col-reverse sm:flex-row gap-2.5">
                <button type="button" className={btn.quiet} onClick={() => setCreditModal(null)}>Cancel</button>
                <button type="submit" form="credit-form" disabled={creditLoading || adding <= 0}
                  className={`${btn.green} flex-1 justify-center disabled:opacity-60`}>
                  {creditLoading ? 'Adding…' : `Credit ${adding > 0 ? fmt(adding) : 'wallet'}`}
                </button>
              </div>
            )}
          >
            <form id="credit-form" onSubmit={handleCredit} className="space-y-4">
              {/* where the balance stands, and where this lands it */}
              <div className="flex items-center justify-between gap-4 rounded-xl px-4 py-3
                              border border-emerald-200 bg-emerald-50/70
                              dark:border-emerald-500/25 dark:bg-emerald-500/10">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700/70 dark:text-emerald-300/70">Current balance</p>
                  <p className="text-[18px] font-black text-emerald-700 dark:text-emerald-300 leading-tight">{fmt(current)}</p>
                </div>
                <ArrowRight size={16} className="text-emerald-500/70 flex-shrink-0" />
                <div className="text-right">
                  <p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700/70 dark:text-emerald-300/70">After credit</p>
                  <p className="text-[18px] font-black text-emerald-700 dark:text-emerald-300 leading-tight">{fmt(current + adding)}</p>
                </div>
              </div>

              <FormField label="Amount" required hint="in rupees">
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold pointer-events-none">₹</span>
                  <input
                    type="number" min="1" step="0.01" autoFocus
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    placeholder="0.00"
                    className={`${field} pl-8 text-[16px] font-bold`}
                    required
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {[1000, 5000, 10000, 25000, 50000].map((amt) => (
                    <button key={amt} type="button" onClick={() => setCreditAmount(String(amt))}
                      className={`px-3 h-8 rounded-lg text-[12px] font-bold border transition ${
                        String(amt) === String(creditAmount)
                          ? 'border-emerald-400 bg-emerald-50 text-emerald-700 dark:border-emerald-500/60 dark:bg-emerald-500/15 dark:text-emerald-300'
                          : 'border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                      ₹{amt.toLocaleString('en-IN')}
                    </button>
                  ))}
                </div>
              </FormField>

              <FormField label="Source" required>
                <Select
                  clearable={false}
                  value={creditSource}
                  onChange={(v) => setCreditSource(v || 'cash')}
                  options={[
                    { value: 'cash', label: 'Cash' },
                    { value: 'bank_transfer', label: 'Bank transfer' },
                    { value: 'card_machine', label: 'Card machine' },
                    { value: 'admin_adjustment', label: 'Admin adjustment' },
                  ]}
                />
              </FormField>

              <FormField label="Remarks" hint="(optional)">
                <input
                  type="text"
                  value={creditRemarks}
                  onChange={(e) => setCreditRemarks(e.target.value)}
                  placeholder="e.g. Advance payment for June"
                  className={field}
                />
              </FormField>

              <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                The credit is recorded on the wallet ledger under your account and shows up in its history straight away.
              </p>
            </form>
          </Modal>
        );
      })()}

      {/* Edit User Modal */}
      <Modal
        open={showEditUserModal}
        onClose={closeModals}
        icon={UserCog}
        tone="blue"
        title="Edit user"
        subtitle={selectedUser?.name || selectedUser?.username}
        width="max-w-3xl"
        footer={(
          <div className="flex flex-col-reverse sm:flex-row gap-2.5">
            <button type="button" onClick={closeModals} className={btn.quiet}>Cancel</button>
            <button type="submit" form="edit-user" className={`${btn.primary} flex-1 justify-center`}>
              <Save size={16} /> Update user
            </button>
          </div>
        )}
      >
        <form id="edit-user" onSubmit={handleUpdateUser}>
          <FormSection title="Identity" description="Who the account belongs to." icon={UserIcon} tone="blue">
            <FormField label="Customer name" required error={errors.customerName} className="md:col-span-2">
              <input
                type="text"
                value={editUser.customerName}
                onChange={(e) => { setEditUser({ ...editUser, customerName: e.target.value }); setErrors({ ...errors, customerName: '' }); }}
                className={`${field} ${errors.customerName ? 'border-rose-400' : ''}`}
              />
            </FormField>

            <FormField label="Email" required error={errors.email}>
              <input
                type="email"
                value={editUser.email}
                onChange={(e) => { setEditUser({ ...editUser, email: e.target.value }); setErrors({ ...errors, email: '' }); }}
                className={`${field} ${errors.email ? 'border-rose-400' : ''}`}
              />
            </FormField>

            <FormField label="Phone number" required error={errors.phoneNumber}>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[13px] font-bold text-slate-400 pointer-events-none">+91</span>
                <input
                  type="tel"
                  value={editUser.phoneNumber}
                  onChange={(e) => { setEditUser({ ...editUser, phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10) }); setErrors({ ...errors, phoneNumber: '' }); }}
                  className={`${field} pl-12 ${errors.phoneNumber ? 'border-rose-400' : ''}`}
                />
              </div>
            </FormField>

            <FormField label="Website" hint="(optional)" error={errors.website}>
              <input
                type="text"
                value={editUser.website}
                onChange={(e) => { setEditUser({ ...editUser, website: e.target.value }); setErrors({ ...errors, website: '' }); }}
                placeholder="https://example.com"
                className={`${field} ${errors.website ? 'border-rose-400' : ''}`}
              />
            </FormField>
          </FormSection>

          <FormSection title="Access" description="What they sign in with, and what they may reach." icon={KeyRound} tone="amber">
            <FormField label="Username" required error={errors.username}>
              <input
                type="text"
                value={editUser.username}
                onChange={(e) => { setEditUser({ ...editUser, username: e.target.value }); setErrors({ ...errors, username: '' }); }}
                className={`${field} ${errors.username ? 'border-rose-400' : ''}`}
              />
            </FormField>

            <FormField label="Password" hint="(leave blank to keep current)" error={errors.password}>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={editUser.password}
                  onChange={(e) => { setEditUser({ ...editUser, password: e.target.value }); setErrors({ ...errors, password: '' }); }}
                  className={`${field} pr-11 ${errors.password ? 'border-rose-400' : ''}`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-400
                             hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-100 dark:hover:bg-slate-700 transition">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </FormField>

            <FormField label="Role" required>
              <Select
                clearable={false}
                value={editUser.role}
                onChange={(v) => setEditUser({ ...editUser, role: v })}
                options={['super_admin', 'admin', 'accountant', 'employee', 'user'].map((r) => ({
                  value: r,
                  label: r === 'super_admin' ? 'Super Admin' : r === 'user' ? 'User' : r.charAt(0).toUpperCase() + r.slice(1),
                }))}
              />
            </FormField>

            <FormField label="Status" required>
              <Select
                clearable={false}
                value={editUser.status}
                onChange={(v) => setEditUser({ ...editUser, status: v })}
                options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
              />
            </FormField>
          </FormSection>

          {editUser.role === 'admin' && (
            <FormSection title="Document inbox" description="The mailbox this admin's LEO PDFs are fetched from." icon={Mail} tone="emerald">
              <div className="md:col-span-2">
                <EmailListInput
                  value={editUser.emailToFetchPdf}
                  onChange={(emails) => { setEditUser({ ...editUser, emailToFetchPdf: emails }); setErrors({ ...errors, emailToFetchPdf: '' }); }}
                  error={errors.emailToFetchPdf}
                />
              </div>
            </FormSection>
          )}

          {editApiError && (
            <div className="mt-4 px-4 py-3 rounded-xl text-[13px] font-semibold
                            border border-rose-200 bg-rose-50/70 text-rose-600
                            dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
              {editApiError}
            </div>
          )}
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmModal
          open
          onClose={closeModals}
          onConfirm={confirmDeleteUser}
          title="Delete user"
          subtitle={`${selectedUser?.name || 'This account'} loses access immediately.`}
          confirmLabel="Delete user"
        >
          <p className="text-[13px] text-slate-600 dark:text-slate-300">
            Deleting <span className="font-bold text-slate-900 dark:text-white">{selectedUser?.name}</span> cannot be undone.
            Their shipments and invoices stay where they are; only the account goes.
          </p>
        </ConfirmModal>
      )}

      <CustomerInvoicesModal
        open={customerInvoiceListOpen}
        onClose={() => setCustomerInvoiceListOpen(false)}
        onEdit={(invoice) => { setCustomerInvoiceEdit(invoice); setCustomerInvoiceOpen(true); }}
      />
    </AdminShell>
  );
};

export default SuperAdminDashboard;
